import React, { useEffect, useState } from "react";

import { SIGNUP_URL, CONTACT, BROCHURES, TRIAL_DAYS, PROJECTS_DELIVERED } from "./siteConfig";
import {
  PRODUCTS,
  HERO_SHOTS,
  SYMPTOMS,
  DMS_PILLARS,
  WORKFLOW_SHOWCASE,
  TASK_SHOWCASE,
  FEATURES,
  REPORTS,
  RESULTS,
  STEPS,
  MFG_PILLARS,
  MFG_SHOWCASE,
  MFG_FEATURES,
  MFG_STEPS,
  COMPARISON,
} from "./siteContent";
import { SiteHeader, SiteFooter } from "./SiteChrome";
import "./Site.css";

/* Screenshots live in /public/site and are generated at a web-friendly size.
   If one is missing the frame collapses instead of showing a broken image, so a
   half-populated deploy still reads as a finished page. */
function Shot({ src, alt }) {
  const [failed, setFailed] = useState(false);
  if (failed) return null;
  return (
    <div className="site-shot">
      <div className="site-shot-bar" aria-hidden="true">
        <span />
        <span />
        <span />
      </div>
      <img src={src} alt={alt} loading="lazy" onError={() => setFailed(true)} />
    </div>
  );
}

/* Both products are described with the same three blocks, so they share the
   same components — only the content differs. */
function Showcase({ items }) {
  return items.map((f, i) => (
    <div
      className={`site-showcase ${i % 2 ? "is-reversed" : ""} ${i === 0 ? "is-first" : ""}`}
      key={f.tag}
    >
      <div className="site-showcase-copy">
        <p className="site-eyebrow">{f.tag}</p>
        <h3>{f.title}</h3>
        <p>{f.body}</p>
        <ul className="site-ticks">
          {f.points.map((p) => (
            <li key={p}>{p}</li>
          ))}
        </ul>
      </div>
      <div className="site-showcase-media">
        <Shot src={f.image} alt={f.alt} />
      </div>
    </div>
  ));
}

function FeatureGrid({ items }) {
  return (
    <div className="site-grid site-grid-3 site-features">
      {items.map((f) => (
        <article className="site-card" key={f.title}>
          <h3>{f.title}</h3>
          <p>{f.body}</p>
        </article>
      ))}
    </div>
  );
}

/* Sits at the end of each module section: someone who has just read the whole
   thing is the most likely person to want it on paper. */
function BrochureCard({ id }) {
  const b = BROCHURES[id];
  if (!b) return null;
  return (
    <div className="site-brochure">
      <span className="site-brochure-icon" aria-hidden="true">
        PDF
      </span>
      <div className="site-brochure-copy">
        <h3>Take the {b.name} brochure with you</h3>
        <p>
          Every screen on this page at full size — easy to send on to whoever
          else has to approve it.
        </p>
      </div>
      <a className="site-btn site-btn-primary" href={b.href} download>
        Download PDF
      </a>
    </div>
  );
}

function Steps({ items }) {
  return (
    <ol className="site-steps">
      {items.map((s, i) => (
        <li key={s}>
          <span className="site-step-n">{i + 1}</span>
          <p>{s}</p>
        </li>
      ))}
    </ol>
  );
}

export default function SiteHome() {
  const [hero, setHero] = useState(HERO_SHOTS[0].id);
  const heroShot = HERO_SHOTS.find((s) => s.id === hero) || HERO_SHOTS[0];

  useEffect(() => {
    document.title = "DecisionFlow — See every job, every step, every person";
  }, []);

  /* The app shell locks html/body to a non-scrolling 100% viewport and paints
     its own background. Released here for as long as the site is mounted. */
  useEffect(() => {
    document.documentElement.classList.add("site-html");
    document.body.classList.add("site-body");
    return () => {
      document.documentElement.classList.remove("site-html");
      document.body.classList.remove("site-body");
    };
  }, []);

  return (
    <div className="site">
      <SiteHeader />

      <main id="top">
        {/* ---------------------------------------------------------- hero */}
        <section className="site-hero">
          <div className="site-container">
            <p className="site-badge">
              <span className="site-dot" />
              {PROJECTS_DELIVERED} projects delivered
            </p>

            <h1>
              Tasks, workflows, projects and people —{" "}
              <span>all in one place.</span>
            </h1>

            <p className="site-lede">
              The all-in-one alternative to Asana and Jira — tasks, approvals
              and boards, plus the workflows, projects, schedules and reports
              around them. For factories, the same system tracks production,
              stock and dispatch.
            </p>

            <div className="site-cta-row">
              <a className="site-btn site-btn-primary site-btn-lg" href={SIGNUP_URL()}>
                Start your {TRIAL_DAYS}-day free trial
              </a>
              <a className="site-btn site-btn-ghost site-btn-lg" href={`mailto:${CONTACT.email}`}>
                Book a demo
              </a>
            </div>

            <p className="site-cta-note">
              No card needed · Set up in under 30 minutes · Our server or yours
            </p>

            <div className="site-hero-shot">
              <div className="site-tabs" role="tablist" aria-label="Choose a module">
                {HERO_SHOTS.map((s) => (
                  <button
                    key={s.id}
                    type="button"
                    role="tab"
                    aria-selected={hero === s.id}
                    className={`site-tab ${hero === s.id ? "is-active" : ""}`}
                    onClick={() => setHero(s.id)}
                  >
                    {s.label}
                  </button>
                ))}
              </div>

              {/* Keyed so switching remounts: Shot latches a failure in state,
                  and without this a broken first image would hide the second. */}
              <Shot key={heroShot.id} src={heroShot.image} alt={heroShot.alt} />

              <p className="site-hero-caption">
                {heroShot.caption}{" "}
                <a href={`#${heroShot.id}`}>See what it does ↓</a>
              </p>
            </div>
          </div>
        </section>

        {/* ------------------------------------------------------ products */}
        <section className="site-section site-section-slim" id="products">
          <div className="site-container">
            <header className="site-section-head">
              <p className="site-eyebrow">Two versions, one system</p>
              <h2>Pick the one that matches your business</h2>
              <p>
                You choose your version when you sign up, and DecisionFlow
                becomes that product. Same login, same settings, same price.
              </p>
            </header>

            <div className="site-grid site-grid-2">
              {PRODUCTS.map((p) => (
                <a className="site-card site-product" href={`#${p.id}`} key={p.id}>
                  <span className="site-product-tag">{p.tag}</span>
                  <h3>{p.name}</h3>
                  <p className="site-product-who">{p.who}</p>
                  <p>{p.body}</p>
                  <span className="site-product-link">See what it does →</span>
                </a>
              ))}
            </div>
          </div>
        </section>

        {/* ------------------------------------------------------- problem */}
        <section className="site-section site-section-tint" id="problem">
          {/* The question leads. Six unexplained complaints, and only then the
              reason they are on the page, made the reader work backwards. */}
          <div className="site-container">
            <header className="site-section-head">
              <p className="site-eyebrow">Why DecisionFlow</p>
              <h2>Does any of this sound like your week?</h2>
              <p>
                All six come from the same thing — the way you work is not
                inside the software you use. DecisionFlow puts it there.
              </p>
            </header>

            <div className="site-grid site-grid-3">
              {SYMPTOMS.map((s) => (
                <article className="site-card site-symptom" key={s.title}>
                  <h3>{s.title}</h3>
                  <p>{s.body}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* ----------------------------------------------------------- dms */}
        <section className="site-section" id="dms">
          <div className="site-container">
            <header className="site-section-head">
              <p className="site-eyebrow">Project & Document Management</p>
              <h2>Everything you'd use Asana or Jira for. And everything else, in the same place.</h2>
              <p>
                Most teams run tasks in one tool, projects in a spreadsheet,
                shifts and leave in another sheet, and reports by hand.
                DecisionFlow packs all of it into one system — the same people,
                the same projects, the same reports.
              </p>
            </header>

            <div className="site-grid site-grid-2 site-pillars">
              {DMS_PILLARS.map((p) => (
                <a className="site-card site-pillar site-pillar-link" href={`#${p.id}`} key={p.id}>
                  <h3>{p.title}</h3>
                  <p className="site-pillar-tagline">{p.tagline}</p>
                  <p>{p.body}</p>
                  <ul className="site-ticks">
                    {p.points.map((pt) => (
                      <li key={pt}>{pt}</li>
                    ))}
                  </ul>
                  <span className="site-product-link">See how it works ↓</span>
                </a>
              ))}
            </div>

            <header className="site-subhead" id="tasks">
              <p className="site-eyebrow">Task management</p>
              <h2>Everything you'd use Asana or Jira for</h2>
            </header>
            <Showcase items={TASK_SHOWCASE} />

            <header className="site-subhead" id="workflows">
              <p className="site-eyebrow">Workflows & projects</p>
              <h2>And the process, the people and the schedule behind it</h2>
            </header>
            <Showcase items={WORKFLOW_SHOWCASE} />

            <FeatureGrid items={FEATURES} />
            <BrochureCard id="dms" />
          </div>
        </section>

        {/* ------------------------------------------------------- reports */}
        <section className="site-section site-section-dark" id="reports">
          <div className="site-container">
            <header className="site-section-head">
              <p className="site-eyebrow">Reporting · Project & Document Management</p>
              <h2>The reports are ready before you ask</h2>
              <p>
                Four reports you can download as PDF, built from the work your
                team recorded as it happened — not typed up the night before.
              </p>
            </header>

            <div className="site-grid site-grid-4">
              {REPORTS.map((r, i) => (
                <article className="site-card site-report" key={r.title}>
                  <span className="site-report-n">{String(i + 1).padStart(2, "0")}</span>
                  <h3>{r.title}</h3>
                  <p>{r.body}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* ------------------------------------------------------ dms flow */}
        <section className="site-section">
          <div className="site-container">
            <header className="site-section-head">
              <p className="site-eyebrow">How it works</p>
              <h2>A project from brief to delivered</h2>
            </header>
            <Steps items={STEPS} />
          </div>
        </section>

        {/* ------------------------------------------------------- results */}
        {/* One client's numbers, unattributed. Figures and caveats live in
            siteContent.js#RESULTS. */}
        <section className="site-section site-section-dark" id="results">
          <div className="site-container">
            <header className="site-section-head">
              <p className="site-eyebrow">Results</p>
              <h2>What changed for one client</h2>
              <p>
                A service business running its projects on DecisionFlow,
                measured from its own records — its first months against the
                months since.
              </p>
            </header>

            <div className="site-grid site-grid-4">
              {RESULTS.map((r) => (
                <article className="site-card site-report site-result" key={r.label}>
                  <span className="site-result-figure">{r.figure}</span>
                  <h3>{r.label}</h3>
                  <p>{r.body}</p>
                </article>
              ))}
            </div>
            <p className="site-result-note">
              Figures from one client's own records over its first six months
              on DecisionFlow. The client is not named.
            </p>
          </div>
        </section>

        {/* ---------------------------------------------------- comparison */}
        <section className="site-section site-section-tint" id="compare">
          <div className="site-container">
            <header className="site-section-head">
              <p className="site-eyebrow">Compared to what you use today</p>
              <h2>A task app tracks the tasks. DecisionFlow runs the whole job.</h2>
            </header>

            <div className="site-table-wrap">
              <table className="site-table">
                <thead>
                  <tr>
                    <th />
                    {COMPARISON.columns.map((c, i) => (
                      <th key={c} className={i === COMPARISON.highlight ? "is-highlight" : ""}>
                        {c}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {COMPARISON.rows.map((row) => (
                    <tr key={row.label}>
                      <th scope="row">{row.label}</th>
                      {row.cells.map((cell, i) => (
                        <td
                          key={`${row.label}-${i}`}
                          className={i === COMPARISON.highlight ? "is-highlight" : ""}
                        >
                          {cell}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {/* On a phone the table is wider than the screen and the column it
                argues for is the one off the right edge — without this nobody
                knows to swipe. */}
            <p className="site-table-hint">Swipe to compare →</p>
          </div>
        </section>

        {/* --------------------------------------------------- manufacturing */}
        <section className="site-section" id="manufacturing">
          <div className="site-container">
            <header className="site-section-head">
              <p className="site-eyebrow">Manufacturing & Production Tracking</p>
              <h2>Your whole plant, not just the production line</h2>
              <p>
                Four things that usually sit in four different places — the
                orders, the stock, the daily checks and the dispatch — running
                as one system, with the people doing the work reporting from the
                phone already in their pocket.
              </p>
            </header>

            <div className="site-grid site-grid-4 site-pillars">
              {MFG_PILLARS.map((p, i) => (
                <article className="site-card site-pillar" key={p.title}>
                  <span className="site-pillar-n">{String(i + 1).padStart(2, "0")}</span>
                  <h3>{p.title}</h3>
                  <p>{p.body}</p>
                </article>
              ))}
            </div>

            <Showcase items={MFG_SHOWCASE} />
            <FeatureGrid items={MFG_FEATURES} />
            <BrochureCard id="manufacturing" />
          </div>
        </section>

        {/* ------------------------------------------------------ mfg flow */}
        <section className="site-section site-section-tint">
          <div className="site-container">
            <header className="site-section-head">
              <p className="site-eyebrow">How it works</p>
              <h2>An order from booked to delivered</h2>
            </header>
            <Steps items={MFG_STEPS} />
          </div>
        </section>

        {/* ------------------------------------------------------ included */}
        {/* Rates are quoted by sales rather than published. The list stays —
            it is the only place several of these claims are made — but the
            reader is sent to a person for the number. */}
        <section className="site-section" id="included">
          <div className="site-container">
            <header className="site-section-head">
              <p className="site-eyebrow">What you get</p>
              <h2>One plan. Everything included.</h2>
              <p>
                No plans to compare and nothing sold on top. Every customer gets
                all of whichever version they run.
              </p>
            </header>

            <div className="site-plan-card">
              <div className="site-plan-main">
                <a className="site-btn site-btn-primary site-btn-lg" href={SIGNUP_URL()}>
                  Start free trial
                </a>
                <p className="site-cta-note">
                  {TRIAL_DAYS} days free. No card needed.
                </p>
                <a
                  className="site-btn site-btn-ghost site-btn-lg"
                  href={`mailto:${CONTACT.email}?subject=DecisionFlow pricing`}
                >
                  Ask us what it costs
                </a>
                <p className="site-cta-note">
                  Tell us how many people will use it and we will send you a
                  figure.
                </p>
              </div>

              <ul className="site-ticks site-plan-list">
                <li>Everything in your version, and every future update</li>
                <li>As many projects, orders, steps and roles as you need</li>
                <li>Tasks, approvals and project boards built in</li>
                <li>Reports you can download as PDF</li>
                <li>WhatsApp and Telegram updates included</li>
                <li>On our servers, or on yours</li>
                <li>Setup and training in under 30 minutes</li>
              </ul>
            </div>
          </div>
        </section>

        {/* ----------------------------------------------------- final cta */}
        <section className="site-final">
          <div className="site-container">
            <h2>See it running on your own way of working</h2>
            <p>
              Tell us how a job moves through your business today, and we will
              show you the same thing running in DecisionFlow.
            </p>
            <div className="site-cta-row">
              <a className="site-btn site-btn-light site-btn-lg" href={SIGNUP_URL()}>
                Start free trial
              </a>
              <a className="site-btn site-btn-outline site-btn-lg" href={`mailto:${CONTACT.email}`}>
                Talk to us
              </a>
            </div>
          </div>
        </section>
      </main>

      <SiteFooter />
    </div>
  );
}
